import { BACKEND_URL } from './api';

/**
 * Resolve a font stylesheet URL returned by the API. Google Fonts come back
 * as absolute URLs; uploaded fonts are served under /api. On azamein.com that is a
 * same-origin path (the site proxies /api and /uploads), so no cross-site font request
 * that a filter on the customer's computer can block; elsewhere (local dev) the backend URL.
 */
export const resolveFontUrl = (fontUrl) => {
  if (!fontUrl) return '';
  if (fontUrl.startsWith('http')) return fontUrl;
  if (fontUrl.startsWith('/api')) {
    const sameOrigin = typeof window !== 'undefined' && /(^|\.)azamein\.com$/.test(window.location.hostname);
    return sameOrigin ? fontUrl : `${BACKEND_URL}${fontUrl}`;
  }
  return fontUrl;
};

/** Inject a <link rel="stylesheet"> for each font that isn't loaded yet. */
export const loadFontStylesheets = (fonts) => {
  if (!Array.isArray(fonts)) return;
  fonts.forEach((font) => {
    const href = resolveFontUrl(font?.font_url);
    if (!href) return;
    const alreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(
      (link) => link.getAttribute('href') === href
    );
    if (alreadyLoaded) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  });
};

/**
 * Built-in families offered by the design studio (docs/INVITATION_DESIGN_SPEC.md).
 * `axes` is the Google Fonts css2 axis spec for the weights the server has.
 */
export const EDITOR_FONTS = [
  { family: 'Frank Ruhl Libre', axes: 'wght@400;700;900', hebrew: true },
  { family: 'David Libre', axes: 'wght@400;700', hebrew: true },
  { family: 'Suez One', axes: '', hebrew: true },
  { family: 'Secular One', axes: '', hebrew: true },
  { family: 'Rubik', axes: 'wght@400;700;900', hebrew: true },
  { family: 'Heebo', axes: 'wght@400;700', hebrew: true },
  { family: 'Bellefair', axes: '', hebrew: true },
  { family: 'Karantina', axes: 'wght@400;700', hebrew: true },
  { family: 'Amatic SC', axes: 'wght@400;700', hebrew: true },
  { family: 'Playfair Display', axes: 'ital,wght@0,400;0,700;1,400;1,700', hebrew: false },
  { family: 'Great Vibes', axes: '', hebrew: false },
  { family: 'Manrope', axes: 'wght@400;700', hebrew: false },
  // ornaments, stars, flourishes, frames: use through the Glyphs panel
  { family: 'Noto Sans Symbols 2', axes: '', hebrew: false, decorative: true },
];

const EDITOR_FONT_BY_FAMILY = Object.fromEntries(EDITOR_FONTS.map((f) => [f.family, f]));

const googleFamilyParam = ({ family, axes }) =>
  `family=${family.trim().replace(/ /g, '+')}${axes ? `:${axes}` : ''}`;

const injectStylesheet = (href, marker) => {
  if (document.querySelector(`link[data-font="${marker}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.font = marker;
  document.head.appendChild(link);
};

/** Load every built-in editor font in one Google Fonts request. */
export const loadEditorFonts = () => {
  const params = EDITOR_FONTS.map(googleFamilyParam).join('&');
  injectStylesheet(`https://fonts.googleapis.com/css2?${params}&display=swap`, 'editor-all');
};

/**
 * Load the built-in fonts used by a design (read-only previews only need the
 * families they draw). Unknown families are ignored: admin fonts are loaded
 * from /api/fonts by the pages that list them.
 */
export const ensureDesignFonts = (families = []) => {
  if (document.querySelector('link[data-font="editor-all"]')) return;
  new Set(families).forEach((family) => {
    const font = EDITOR_FONT_BY_FAMILY[family];
    if (!font) return;
    injectStylesheet(`https://fonts.googleapis.com/css2?${googleFamilyParam(font)}&display=swap`, family);
  });
};
